import React, { useState, useEffect, useContext } from 'react'
import { ContentLoading } from '@ui'
import api, { ICurrentUser, ISpace, IUserSettings } from '@api'
import { flatten, uniqBy } from 'lodash'

const persistedState = {
  user: null,
  loading: true,
}
export const UserContext = React.createContext<IUseCurrentUser>({
  ...persistedState,
  reload: () => undefined,
  updateUserSettings: () => undefined,
})

export function UserContextProvider(props: any) {
  const { user, loading, reload, updateUserSettings } = useCurrentUser()

  return loading ? (
    <ContentLoading />
  ) : (
    <UserContext.Provider value={{ user, reload, updateUserSettings }} {...props} />
  )
}

export function useUser() {
  return useContext(UserContext)
}

interface IState {
  user: ICurrentUser | null
  loading: boolean
}
export interface IUseCurrentUser extends IState {
  reload: () => void
  updateUserSettings: (settings: Partial<IUserSettings>) => void
}
function useCurrentUser(): IUseCurrentUser {
  const [state, setState] = useState<IState>(persistedState)

  const load = () => {
    api.user
      .current()
      .then((user) => {
        const {
          bookingManagerSetting,
          employee: { role, name, email, id: employeeId, spaceAssignment },
          ...rest
        } = user
        const {
          manage_all,
          managed_departments = [],
          managed_employees = [],
        } = {
          ...bookingManagerSetting,
        }

        const allEmployeesFromDepartment = managed_departments.map(
          ({ employeeConnection: { edges } }) => edges.map(({ node }) => node)
        )
        const allManagableEmployees = uniqBy(
          [
            ...flatten(allEmployeesFromDepartment),
            ...managed_employees,
            { id: employeeId, name: name, email: email }, // add self for Booking manager
          ],
          'id'
        )
        setState({
          user: {
            ...user,
            bookingManagerSetting: { ...user.bookingManagerSetting, allManagableEmployees },
            role,
            space: spaceAssignment?.space as unknown as ISpace,
            name,
            context: { ...user.context, settings: { ...user.settings } },
          },
          loading: false,
        })
      })
      .catch(() => setState({ user: null, loading: false }))
  }

  const updateUserSettings: IUseCurrentUser['updateUserSettings'] = async (settings) => {
    if (state && state.user) {
      setState({
        ...state,
        user: {
          ...state.user,
          context: {
            ...state.user.context,
            settings: { ...state.user.context.settings, ...settings },
          },
        },
        loading: false,
      })
    }

    try {
      const settingsData = await api.user.updateSettings(settings)
      if (!state || !state.user) return

      setState({
        ...state,
        user: {
          ...state.user,
          context: {
            ...state.user.context,
            settings: { ...state.user.context.settings, ...settingsData },
          },
        },
        loading: false,
      })
    } catch (error: any) {
      setState({ ...state, loading: false })
    }
  }

  useEffect(load, [])

  return {
    ...state,
    reload: load,
    updateUserSettings,
  }
}
